// Components/CustomSwiper.tsx
import { styled } from "@mui/material";
import { Swiper as OriginalSwiper, SwiperProps } from "swiper/react";
import { ReactNode, useRef, useEffect } from "react";

interface CustomSwiperProps extends SwiperProps {
  children: ReactNode;
  className?: string;
  enableTouchpadScroll?: boolean;
  autoplay?: any;
  [key: string]: any;
}

const StyledSwiper = styled(OriginalSwiper)(({ theme }) => ({
  width: "100% !important",
  overflow: "hidden",
  cursor: "grab",

  "&:active": {
    cursor: "grabbing",
  },

  "& .swiper-wrapper": {
    display: "flex",
    width: "100%",
  },

  "& .swiper-slide": {
    height: "auto",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
    flex: "0 0 auto",
    maxWidth: "100%",
    minWidth: 0,
  },

  [theme.breakpoints.down("lg")]: {
    "& .swiper-slide": {
      maxWidth: "calc(33.333% - 5.33px)",
    },
  },

  [theme.breakpoints.down("md")]: {
    "& .swiper-slide": {
      maxWidth: "calc(50% - 4px)",
    },
  },

  [theme.breakpoints.down("sm")]: {
    "& .swiper-slide": {
      maxWidth: "100% !important",
      width: "100% !important",
    },
  },
}));

const CustomSwiper = ({
  children,
  enableTouchpadScroll = true,
  autoplay,
  onSwiper,
  ...props
}: CustomSwiperProps) => {
  const swiperInstanceRef = useRef<any>(null);
  const autoplayTimeoutRef = useRef<NodeJS.Timeout>();

  const handleSwiperInit = (swiper: any) => {
    swiperInstanceRef.current = swiper;
    if (onSwiper) onSwiper(swiper);
  };

  useEffect(() => {
    if (!enableTouchpadScroll || !swiperInstanceRef.current) return;

    const swiper = swiperInstanceRef.current;
    const swiperEl = swiper.el;

    if (!swiperEl) return;

    let isScrolling = false;

    const handleWheel = (e: WheelEvent) => {
      // Chỉ xử lý nếu scroll trong swiper area
      if (!swiperEl.contains(e.target as Node)) return;

      // Ngăn scroll page khi hover vào swiper
      const shouldPreventDefault =
        Math.abs(e.deltaX) > 10 || (e.shiftKey && Math.abs(e.deltaY) > 10);

      if (shouldPreventDefault) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Chỉ xử lý nếu không đang scroll
      if (isScrolling) return;

      // Xác định hướng scroll
      let deltaX = e.deltaX;
      let deltaY = e.deltaY;

      // Nếu scroll dọc mà giữ shift, chuyển thành scroll ngang
      if (e.shiftKey && Math.abs(deltaY) > Math.abs(deltaX)) {
        deltaX = deltaY;
      }

      // Threshold để tránh scroll nhạy cảm quá
      const threshold = 15;

      if (Math.abs(deltaX) > threshold) {
        isScrolling = true;

        // Tạm dừng autoplay khi user scroll
        if (autoplay && swiper.autoplay && swiper.autoplay.running) {
          swiper.autoplay.stop();
        }

        if (deltaX > 0) {
          swiper.slideNext();
        } else {
          swiper.slidePrev();
        }

        // Resume autoplay sau 3 giây không có interaction
        if (autoplay) {
          clearTimeout(autoplayTimeoutRef.current);
          autoplayTimeoutRef.current = setTimeout(() => {
            if (swiper.autoplay && !swiper.autoplay.running) {
              swiper.autoplay.start();
            }
          }, 3000); // Resume sau 3 giây
        }

        // Reset scroll flag
        setTimeout(() => {
          isScrolling = false;
        }, 200);
      }
    };

    // Mouse enter/leave để handle autoplay
    const handleMouseEnter = () => {
      if (autoplay && swiper.autoplay && swiper.autoplay.running) {
        swiper.autoplay.stop();
      }
    };

    const handleMouseLeave = () => {
      if (autoplay && swiper.autoplay && !swiper.autoplay.running) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = setTimeout(() => {
          swiper.autoplay.start();
        }, 500);
      }
    };

    // Add event listeners
    swiperEl.addEventListener("wheel", handleWheel, { passive: false });
    swiperEl.addEventListener("mouseenter", handleMouseEnter);
    swiperEl.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      if (swiperEl) {
        swiperEl.removeEventListener("wheel", handleWheel);
        swiperEl.removeEventListener("mouseenter", handleMouseEnter);
        swiperEl.removeEventListener("mouseleave", handleMouseLeave);
      }
      clearTimeout(autoplayTimeoutRef.current);
    };
  }, [enableTouchpadScroll, autoplay]);

  return (
    <StyledSwiper onSwiper={handleSwiperInit} autoplay={autoplay} {...props}>
      {children}
    </StyledSwiper>
  );
};

export default CustomSwiper;
